import { NocfoPlan, NocfoPlanSource, VatRateLabel, VatPostingMethod } from '@constants'
import { network, saveBlobAsFile } from '@utils'
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { BusinessIdentifier } from './businessIdentifiers'
import { ITranslatedName } from './accounts'
import {
  DefaultQueryParams,
  handleReturn,
  MutationFunction,
  PaginatedResponse,
  QueryFunction
} from './root'

export interface SoftReminderStep {
  days_after_due: number
  message: ITranslatedName[]
}

export interface IVatRateOption {
  label: VatRateLabel
  rate: number
}

export interface IVatConfig {
  effective_from: string
  default_vat_rate_label: string
  vat_rate_options: IVatRateOption[]
}

interface ICountryFeature {
  enable_banking?: boolean
  salaxy?: boolean
  verohallinto?: boolean
  vat_external_reporting?: boolean
  invoicing?: boolean
  salaries?: boolean
}

export interface ICountryConfig {
  default_language?: string
  invoicing_languages?: string[]
  vat_configs: IVatConfig[]
  feature: ICountryFeature
}

export enum BusinessForms {
  FI_OY = 'FI_OY',
  FI_OYJ = 'FI_OYJ',
  FI_TMI = 'FI_TMI',
  FI_KY = 'FI_KY',
  FI_OK = 'FI_OK',
  FI_AY = 'FI_AY',
  FI_AS_OY = 'FI_AS_OY',
  FI_KOY = 'FI_KOY',
  FI_NY = 'FI_NY',
  FI_YHD = 'FI_YHD',
  DE_UG = 'DE_UG',
  DE_GMBH = 'DE_GMBH',
  DEMO = 'DEMO'
}

export enum BusinessCountry {
  FI = 'fi',
  DE = 'de'
}

export enum BusinessOnboardingStepKey {
  BANK_INTEGRATION = 'bank_integration',
  APPROVE_TRANSACTIONS = 'approve_transactions',
  HEAR_ABOUT_US = 'hear_about_us',
  ACCRUAL_ACCOUNTING = 'accrual_accounting',
  MOBILE_APP_DOWNLOAD = 'mobile_app_download',
  FILES_AND_INVOICES = 'files_and_invoices',
  HAS_PREVIOUS_ACCOUNTING = 'has_previous_accounting',
  LUCA_MESSAGE = 'luca_message',
  QUESTIONS = 'questions'
}

export interface IBusinessOnboardingStepChild {
  id: number
  step: string
  label: string
  created_at: string
  completed_at: string | null
  dismissed_at: string | null
}

export interface IBusinessOnboardStepProgress {
  completed: number
  total: number
}

export interface IBusinessOnboardStep {
  group_key: BusinessOnboardingStepKey
  description: string
  header: string
  credit_reward: number
  credits_granted_at: string | null
  progress?: IBusinessOnboardStepProgress | null
  has_bank_connection?: boolean | null
  child_steps: IBusinessOnboardingStepChild[]
}

export type InvoicingDefaultSettlementDate = 'invoice_date' | 'previous_month_end'

export interface IBusiness {
  id: number
  logo: string
  name: string
  slug: string
  business_id: string // Deprecated, use slug instead
  country: BusinessCountry
  country_config: ICountryConfig
  default_vat_period: number
  has_dauerfristverlaengerung?: boolean
  finanzamtsnummer?: string | null
  invoicing_default_penalty_interest: number
  invoicing_default_payment_condition_days: number
  invoicing_default_email_subject?: string | null
  invoicing_default_email_content?: string | null
  invoicing_vat_exemption_reason?: string | null
  has_accrual_based_entries_for_invoicing: boolean
  invoicing_default_settlement_date?: InvoicingDefaultSettlementDate
  form: BusinessForms
  is_agency_business?: boolean
  has_history_before_nocfo: boolean
  fiscal_period_needs_review: boolean
  vat_period_needs_review: boolean
  owner_email: string
  owner_name: string
  period_id: number
  created_at: string
  vat_period_id: number
  coa_size: number
  contact_phone: string
  has_tags: boolean

  identifiers?: BusinessIdentifier[]

  // Mailing address
  has_business_address: boolean
  business_street?: string
  business_city?: string
  business_postal_code?: string
  business_country?: string

  // Invoicing info
  invoicing_email?: string
  invoicing_iban?: string
  finanzkasse_iban?: string | null
  finanzkasse_recipient_name?: string | null
  invoicing_tax_code?: string
  invoicing_bic?: string
  invoicing_contact?: string
  invoicing_street?: string
  invoicing_city?: string
  invoicing_postal_code?: string
  invoicing_country?: string
  debt_collection_delay_days_company?: number
  debt_collection_delay_days_person?: number
  can_invoice: boolean
  is_billable?: boolean
  is_new_invoicer: boolean
  is_automatic_debt_collection_enabled: boolean
  is_automatic_soft_reminder_enabled: boolean
  soft_reminder_steps: SoftReminderStep[]

  // Onboarding stuff
  onboarding_updated_invoicing_settings: boolean
  onboarding_created_invoicing_contact: boolean
  onboarding_created_invoicing_product: boolean
  onboarding_registered_to_kravia: boolean

  // e-invoicing
  einvoicing_enabled: boolean
  usage_billed_features_enabled: boolean
  einvoicing_address?: string
  einvoicing_operator?: string
  einvoicing_operator_name?: string | null
  is_eligible_for_einvoicing: boolean

  // Salaxy
  salaxy_enabled: boolean
  salaxy_verified: boolean
  salaxy_account_id?: string
  has_accrual_based_salaries: boolean
  has_salaxy_holiday_accruals: boolean

  // Holvi
  holvi_bookkeeping_api_enabled: boolean
  holvi_bookkeeping_api_staled: boolean
  holvi_nocfo_connection_enabled: boolean
  show_bank_integration_teaser: boolean

  // Kravia
  kravia_enabled: boolean
  kravia_customer_id?: number

  // Stripe
  stripe_customer_id: string
  stripe_subscription_id: string
  stripe_plan_status: undefined | 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing'
  billing_lock_reason: null | 'manual' | 'stripe_unpaid'
  stripe_payg_checkout_enabled: boolean
  credit_cap: number | null
  luca_payg_allowed?: boolean
  luca_payg_available?: boolean

  // Subscription plan
  subscription_plan: NocfoPlan
  subscription_source: NocfoPlanSource

  // Default accounts
  account_yle_tax: number
  account_tax_deferrals: number
  account_income_tax_rec: number
  account_income_tax_lia: number
  account_previous_profit: number
  account_vat_liabilities: number
  account_vat_accrual_adjustment: number
  account_vat_receivables: number
  account_trade_receivables: number
  account_payables: number
  account_cash_payment: number

  // VAT settings
  vat_posting_method: VatPostingMethod
  vat_reporting_method: string

  // Demo
  demo_days_left: number

  // Verohallinto
  verohallinto_latest_reporter_full_name: string
  verohallinto_latest_reporter_phone_number: string

  // Onboarding cards
  onboard_steps: IBusinessOnboardStep[]
}

export type BillingLockReason = NonNullable<IBusiness['billing_lock_reason']>

export const isBusinessBillingLocked = (
  business?: Pick<IBusiness, 'billing_lock_reason'> | null
): boolean => Boolean(business?.billing_lock_reason)

export const canFetchBusinessFeatures = (
  business?: Pick<IBusiness, 'billing_lock_reason'> | null
): boolean => business != null && !isBusinessBillingLocked(business)

export const fetchBusinesses: QueryFunction<void, PaginatedResponse<IBusiness>> = async (
  _,
  params = null
) => {
  const url = `/v1/business/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const useBusinesses = (
  params?: DefaultQueryParams,
  rqProps?: Partial<
    UseQueryOptions<
      PaginatedResponse<IBusiness>,
      any,
      PaginatedResponse<IBusiness>,
      (string | DefaultQueryParams | undefined)[]
    >
  >
): UseQueryResult<PaginatedResponse<IBusiness>> =>
  useQuery({
    queryKey: ['businesses', params],
    queryFn: () => fetchBusinesses(undefined, params ?? null),
    ...(rqProps || {})
  })

interface FetchBusinessProps {
  slug: string
}

export const fetchBusiness: QueryFunction<FetchBusinessProps, IBusiness> = async ({ slug }) => {
  const url = `/v1/business/${slug}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

interface UpdateBusinessProps {
  slug: string
}

export const updateBusiness: MutationFunction<UpdateBusinessProps, IBusiness> = async (
  { slug },
  requestData
) => {
  const url = `/v1/business/${slug}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export interface SoftReminderEmailPreview {
  subject: string
  html: string
  attachment_name: string
}

export const previewSoftReminderEmail = async ({
  slug,
  language,
  message
}: {
  slug: string
  language: string
  message: string
}): Promise<SoftReminderEmailPreview> => {
  const url = `/v1/invoicing/${slug}/soft-reminder-email-preview/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: { language, message },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface CreateBusinessProps {
  coaSize: number
  autoInitialize?: boolean
}

export const createBusiness: MutationFunction<CreateBusinessProps, IBusiness> = async (
  { coaSize, autoInitialize },
  requestData
) => {
  const url = `/v1/business/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    params: {
      coa_size: coaSize,
      ...(autoInitialize && { auto_initialize: 'true' })
    },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteBusiness: MutationFunction<UpdateBusinessProps, void> = async ({ slug }) => {
  const url = `/v1/business/${slug}/`
  await network.httpClient.request({
    url,
    method: 'DELETE'
  })
}

export interface CreateNYBusinessProps {
  name: string
}

export type GermanBusinessForm = BusinessForms.DE_UG | BusinessForms.DE_GMBH

export const isGermanBusinessForm = (form: string | undefined | null): form is GermanBusinessForm =>
  form === BusinessForms.DE_UG || form === BusinessForms.DE_GMBH

export interface CreateGermanBusinessProps {
  name: string
  form: GermanBusinessForm
  default_vat_period?: number
  has_history_before_nocfo?: boolean
  is_vat_liable?: boolean
  handelsregisternummer?: string
  vat_code?: string
  steuernummer?: string
  business_street?: string
  business_city?: string
  business_postal_code?: string
  business_country?: string
}

export const createGermanBusiness = async (
  requestData: CreateGermanBusinessProps
): Promise<IBusiness> => {
  const url = '/v1/business/de-onboard/'
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const createNYBusiness: MutationFunction<void, CreateNYBusinessProps> = async (
  _,
  requestData
) => {
  const url = '/v1/business/ny-onboard/'
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export interface ICreateDemoBusinessProps {
  useMockData: boolean
}

export const createDemoBusiness: MutationFunction<
  ICreateDemoBusinessProps,
  CreateNYBusinessProps
> = async ({ useMockData }) => {
  const url = '/v1/business/demo-onboard/'
  const { status, data } = await network.httpClient.request({
    url,
    data: { use_mock_data: useMockData },
    method: 'POST',
    validateStatus: () => true,
    timeout: 60000
  })
  return handleReturn({ status, data })
}

interface UploadLogoParams {
  slug: string
}

export const uploadLogo = async ({ slug }: UploadLogoParams, file: File, signal?: AbortSignal) => {
  const url = `/v1/business/${slug}/logo/filename/`
  const { data } = await network.httpClient.put(url, file, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal
  })
  return data
}

export const deleteLogo: MutationFunction<UploadLogoParams, void> = async ({ slug }) => {
  const url = `/v1/business/${slug}/logo/`
  await network.httpClient.request({ url, method: 'DELETE' })
}

export type EinvoicingSheetLanguage = 'fi' | 'sv' | 'en'

export const EINVOICING_SHEET_LANGUAGES: EinvoicingSheetLanguage[] = ['fi', 'en', 'sv']

export const sendEinvoicingAddressSheet = async ({
  slug,
  recipients,
  language,
  message
}: FetchBusinessProps & {
  recipients: string[]
  language: EinvoicingSheetLanguage
  message?: string
}): Promise<{ sent: number }> => {
  const { data } = await network.httpClient.request({
    url: `/v1/business/${slug}/einvoicing/address_sheet/send/`,
    method: 'POST',
    data: {
      recipients,
      language,
      message
    }
  })
  return data
}

const filenameFromContentDisposition = (header: string | undefined, fallback: string): string => {
  if (!header) return fallback
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      return fallback
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header)
  if (quoted?.[1]) return quoted[1]
  const plain = /filename=([^;]+)/i.exec(header)
  return plain?.[1]?.trim() || fallback
}

export const fetchEinvoicingAddressSheet = async ({
  slug,
  language
}: FetchBusinessProps & { language: EinvoicingSheetLanguage }): Promise<void> => {
  const response = await network.httpClient.request({
    url: `/v1/business/${slug}/einvoicing/address_sheet/`,
    method: 'GET',
    params: { language },
    responseType: 'blob'
  })

  const filename = filenameFromContentDisposition(
    response.headers?.['content-disposition'],
    'Invoicing_details.pdf'
  )
  saveBlobAsFile(response.data, filename)
}

export const enableEinvoicing: MutationFunction<FetchBusinessProps, void> = async ({ slug }) => {
  const url = `/v1/business/${slug}/einvoicing/enable/`
  await network.httpClient.request({
    url,
    method: 'POST',
    data: null
  })
}

export const disableEinvoicing: MutationFunction<FetchBusinessProps, void> = async ({ slug }) => {
  const url = `/v1/business/${slug}/einvoicing/disable/`
  await network.httpClient.request({
    url,
    method: 'POST',
    data: null
  })
}

interface DismissBankIntegrationTeaserProps {
  slug: string
}

export const dismissBankIntegrationTeaser: MutationFunction<
  DismissBankIntegrationTeaserProps,
  void
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/bank_integration_teaser/dismiss/`
  await network.httpClient.request({
    url,
    method: 'POST',
    data: null
  })
}
